import { useMemo } from "react";

import { Colors } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { getTimeZones } from "@vvo/tzdb";
import Select from "react-select";

import { Box } from "@/components/layout/flexbox";
import { SelectFn, SelectOption } from "@/types/select";

const commonTimeZones = [
  // United States
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Adak",
  "Pacific/Honolulu",
  "America/Puerto_Rico",
  // US territories
  "America/St_Thomas",
  "Pacific/Guam",
  // Europe
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Moscow",
  // Middle East
  "Asia/Jerusalem",
  // Asia/Pacific
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

function getSortedTimeZones() {
  const allTimeZones = getTimeZones();

  // Create a Map to store the index of each common time zone
  const commonTimeZonesOrder = new Map(
    commonTimeZones.map((tz, index) => [tz, index]),
  );

  // Sort the time zones
  const sortedTimeZones = allTimeZones.sort((a, b) => {
    const indexA = commonTimeZonesOrder.get(a.name);
    const indexB = commonTimeZonesOrder.get(b.name);

    if (indexA !== undefined && indexB !== undefined) {
      // Both are in commonTimeZones, sort by their order in that list
      return indexA - indexB;
    } else if (indexA !== undefined) {
      // Only a is in commonTimeZones, it should come first
      return -1;
    } else if (indexB !== undefined) {
      // Only b is in commonTimeZones, it should come first
      return 1;
    } else {
      // Neither is in commonTimeZones, sort by offset
      return a.rawOffsetInMinutes - b.rawOffsetInMinutes;
    }
  });

  return sortedTimeZones;
}

interface TimeZoneSelectorProps {
  value: string;
  onChange: SelectFn<SelectOption>;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
}

export function TimeZoneSelector({
  value,
  onChange,
  loading = false,
  disabled = false,
  className = "select-time-range",
}: TimeZoneSelectorProps) {
  const options = useMemo(
    () =>
      getSortedTimeZones().map((tz) => ({
        index: tz.rawOffsetInMinutes,
        value: tz.name,
        label: tz.currentTimeFormat,
        group: tz.group,
      })),
    [],
  );

  return (
    <SelectBox>
      <Select
        isDisabled={disabled}
        className={className}
        isLoading={loading}
        value={options.find((o) => o.group.indexOf(value) > -1)}
        onChange={onChange}
        isSearchable={true}
        name="timeZone"
        options={options}
      />
    </SelectBox>
  );
}

const SelectBox = styled(Box)`
  min-width: 120px;
  border-radius: 6px;
  border: none;
  color: ${Colors.BLACK};

  .select-time-range {
    [class$="-control"] {
      border: none;
      border-radius: 6px;
      background-color: ${Colors.LIGHT_GRAY5};
      padding: 9px 4px;
    }

    &.select-time-range-mini {
      [class$="-control"] {
        padding: 4px;
      }
    }
  }
`;
